/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/
import React, {useEffect, useState} from 'react';
import PhoneInput from 'react-phone-number-input';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import PhoneNumberInput from './PhoneNumberInput';
import {getPhoneNumberErrorText, isPossiblePhoneNumber} from './Utils';

type FormValidationPhoneNumberType = {
  isEmptyPhoneNumber?: boolean;
  isInvalidPhoneNumber?: boolean;
}
export interface PhoneNumberProps {
  id?: string;
  formValidation: FormValidationPhoneNumberType
  existingPhoneNumber: string;
  onChange?: Function;
  required?: boolean;
  disabled?: boolean;
  labelText?: string;
  placeholderText?: string;
  autoComplete?: string;
  className?: string;
}

const useStyles = makeStyles(theme => ({
  labelText : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3'),
    paddingLeft  : '41px'
  },
  requiredAsterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  }
}));

/**
 * Reusable component for user inputting an international format. 
 * Any additional props are sent to the phone input.
 */
export const PhoneNumber = ({
  formValidation : {
    isEmptyPhoneNumber,
    isInvalidPhoneNumber
  },
  existingPhoneNumber,
  onChange: parentOnChange,
  required,
  labelText,
  placeholderText,
  id,
  autoComplete = 'off',
  ...props
}: PhoneNumberProps) => {
  const classes = useStyles();
  const [phoneNumber, setPhoneNumber] =
    useState(existingPhoneNumber || undefined);
  const [isEmptyError, setIsEmptyError] = useState(isEmptyPhoneNumber);
  const [hasValidationError, setHasValidationError] = useState(isInvalidPhoneNumber);

  useEffect(() => {
    setIsEmptyError(isEmptyPhoneNumber);
  }, [isEmptyPhoneNumber]);

  useEffect(() => {
    setHasValidationError(isInvalidPhoneNumber);
  }, [isInvalidPhoneNumber]);

  useEffect(() => {
    if (existingPhoneNumber) {
      setPhoneNumber(existingPhoneNumber);
    }
  }, [existingPhoneNumber]);

  useEffect(() => {
    if (phoneNumber) {
      setIsEmptyError(false);
      setHasValidationError(!isPossiblePhoneNumber(phoneNumber));
    } else {
      setIsEmptyError(isEmptyPhoneNumber);
      setHasValidationError(false);
    }
    if (phoneNumber) {
      parentOnChange(phoneNumber, id);
    }
  }, [phoneNumber]);

  return (
    <>
      {labelText &&
          <div className={classes.labelText}>
            {labelText}
            {required && <span className={classes.requiredAsterisk}> *</span>}
          </div>}
      <PhoneInput placeholderText={placeholderText}
        value={existingPhoneNumber}
        required={required}
        autoComplete={autoComplete}
        id={id}
        onChange={setPhoneNumber}
        inputComponent={PhoneNumberInput}
        errorText={getPhoneNumberErrorText(isEmptyError, hasValidationError)}
        hasError={isEmptyError || hasValidationError}
        {...props} />
    </>
  );
};

export default PhoneNumber;
